<script setup>
// O formulário do chat ao vivo, dentro da gaveta. O título mora no cabeçalho da gaveta (v-model:title); o Criar/Salvar
// também (chama `submit`). As seções abrem e fecham (Q1, 8-6 = A) e "Ativada" e "Só em horário comercial" são
// `Switch` (8-5 = B). Props, validação, `prepareCampaignDetails` e o evento `submit` são os do woot.
import { reactive, computed, ref, watch, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { URLPattern } from 'urlpattern-polyfill';

import CampaignMessageEditor from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignMessageEditor.vue';
import FoldingGroup from 'dashboard/components-next/polaris/cards/FoldingGroup.vue';
import SearchSelect from 'dashboard/components-next/polaris/cards/SearchSelect.vue';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from 'dashboard/components-next/polaris/ui/field';
import { Input } from 'dashboard/components-next/polaris/ui/input';
import { Switch } from 'dashboard/components-next/polaris/ui/switch';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['edit', 'create'].includes(value),
  },
  selectedCampaign: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['submit']);
const title = defineModel('title', { type: String, default: '' });

const { t } = useI18n();
const store = useStore();
const uid = useId();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  inboxes: useMapGetter('inboxes/getWebsiteInboxes'),
};

const senderList = ref([]);

const initialState = {
  title: '',
  message: '',
  inboxId: null,
  senderId: 0,
  enabled: true,
  triggerOnlyDuringBusinessHours: false,
  endPoint: '',
  timeOnPage: 10,
};

const state = reactive({ ...initialState });
const baseline = ref(JSON.stringify(initialState));

const urlValidators = {
  shouldBeAValidURLPattern: value => {
    try {
      // eslint-disable-next-line
      new URLPattern(value);
      return true;
    } catch {
      return false;
    }
  },
  shouldStartWithHTTP: value =>
    value ? value.startsWith('https://') || value.startsWith('http://') : false,
};

const validationRules = {
  title: { required, minLength: minLength(1) },
  message: { required, minLength: minLength(1) },
  inboxId: { required },
  senderId: { required },
  endPoint: { required, ...urlValidators },
  timeOnPage: { required },
};

const v$ = useVuelidate(validationRules, state);

const isSaving = computed(
  () => formState.uiFlags.value.isCreating || formState.uiFlags.value.isUpdating
);
const isSubmitDisabled = computed(() => v$.value.$invalid);
const isDirty = computed(() => JSON.stringify(state) !== baseline.value);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const sendersAndBotList = computed(() => [
  { value: 0, label: t('CAMPAIGN.LIVE_CHAT.CARD.CAMPAIGN_DETAILS.BOT') },
  ...mapToOptions(senderList.value, 'id', 'name'),
]);

const formErrors = computed(() => ({
  title: v$.value.title.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TITLE.ERROR')
    : '',
  message: v$.value.message.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.ERROR')
    : '',
  inbox: v$.value.inboxId.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.ERROR')
    : '',
  endPoint: v$.value.endPoint.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.ERROR')
    : '',
  timeOnPage: v$.value.timeOnPage.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TIME_ON_PAGE.ERROR')
    : '',
  sender: v$.value.senderId.$error
    ? t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.ERROR')
    : '',
}));

const resetState = () => {
  Object.assign(state, initialState);
  baseline.value = JSON.stringify(initialState);
  v$.value.$reset();
};

const handleInboxChange = async inboxId => {
  if (!inboxId) {
    senderList.value = [];
    return;
  }

  try {
    const response = await store.dispatch('inboxMembers/get', { inboxId });
    senderList.value = response?.data?.payload ?? [];
  } catch (error) {
    senderList.value = [];
    useAlert(
      error?.response?.message ??
        t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.API.ERROR_MESSAGE')
    );
  }
};

const prepareCampaignDetails = () => ({
  title: state.title,
  message: state.message,
  inbox_id: state.inboxId,
  sender_id: state.senderId || null,
  enabled: state.enabled,
  trigger_only_during_business_hours: state.triggerOnlyDuringBusinessHours,
  trigger_rules: {
    url: state.endPoint,
    time_on_page: state.timeOnPage,
  },
});

// Valida e entrega os dados; o Criar/Salvar da gaveta chama isto. Devolve se havia o que entregar.
const submit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return false;

  emit('submit', prepareCampaignDetails());
  return true;
};

const updateStateFromCampaign = campaign => {
  if (!campaign) return;

  const {
    title: campaignTitle,
    message,
    inbox: { id: inboxId },
    sender,
    enabled,
    trigger_only_during_business_hours: triggerOnlyDuringBusinessHours,
    trigger_rules: { url: endPoint, time_on_page: timeOnPage },
  } = campaign;

  Object.assign(state, {
    title: campaignTitle,
    message,
    inboxId,
    senderId: sender?.id ?? 0,
    enabled,
    triggerOnlyDuringBusinessHours,
    endPoint,
    timeOnPage,
  });
  baseline.value = JSON.stringify(state);
};

// O título é o campo do cabeçalho da gaveta: dois sentidos, sem laço (só escreve quando difere).
watch(
  title,
  value => {
    if (value !== state.title) state.title = value;
  },
  { immediate: true }
);
watch(
  () => state.title,
  value => {
    if (value !== title.value) title.value = value;
  }
);

watch(
  () => state.inboxId,
  newInboxId => {
    if (newInboxId) {
      handleInboxChange(newInboxId);
    }
  },
  { immediate: true }
);

watch(
  () => props.selectedCampaign,
  newCampaign => {
    if (props.mode === 'edit' && newCampaign?.id) {
      updateStateFromCampaign(newCampaign);
    }
  },
  { immediate: true }
);

defineExpose({
  prepareCampaignDetails,
  isSubmitDisabled,
  isDirty,
  isSaving,
  submit,
  resetState,
  state,
});
</script>

<template>
  <form
    class="grid gap-6"
    novalidate
    data-campaign-form="live-chat"
    @submit.prevent="submit"
  >
    <FieldError
      v-if="formErrors.title"
      :errors="[formErrors.title]"
      data-campaign-title-error
    />

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.MESSAGE')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.message">
          <FieldLabel :for="`${uid}-message`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.LABEL') }}
          </FieldLabel>
          <div :id="`${uid}-message`">
            <CampaignMessageEditor
              v-model="state.message"
              :invalid="!!formErrors.message"
              :aria-label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.LABEL')"
              :placeholder="
                t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.MESSAGE.PLACEHOLDER')
              "
              @blur="v$.message.$touch()"
            />
          </div>
          <FieldError
            v-if="formErrors.message"
            :errors="[formErrors.message]"
          />
        </Field>

        <Field :data-invalid="!!formErrors.inbox">
          <FieldLabel :for="`${uid}-inbox`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.LABEL') }}
          </FieldLabel>
          <SearchSelect
            :id="`${uid}-inbox`"
            v-model="state.inboxId"
            :options="inboxOptions"
            :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.LABEL')"
            :placeholder="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.INBOX.PLACEHOLDER')"
            :aria-invalid="!!formErrors.inbox"
          />
          <FieldError v-if="formErrors.inbox" :errors="[formErrors.inbox]" />
        </Field>

        <Field :data-invalid="!!formErrors.sender">
          <FieldLabel :for="`${uid}-sender`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.LABEL') }}
          </FieldLabel>
          <SearchSelect
            :id="`${uid}-sender`"
            v-model="state.senderId"
            :options="sendersAndBotList"
            :disabled="!state.inboxId"
            :label="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.LABEL')"
            :placeholder="
              t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.SENT_BY.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.sender"
          />
          <FieldError v-if="formErrors.sender" :errors="[formErrors.sender]" />
        </Field>
      </FieldGroup>
    </FoldingGroup>

    <FoldingGroup :title="t('CAMPAIGN.DRAWER.SECTIONS.WHEN')">
      <FieldGroup class="gap-4">
        <Field :data-invalid="!!formErrors.endPoint">
          <FieldLabel :for="`${uid}-url`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.LABEL') }}
          </FieldLabel>
          <Input
            :id="`${uid}-url`"
            v-model="state.endPoint"
            type="url"
            :placeholder="
              t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.END_POINT.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.endPoint"
            @blur="v$.endPoint.$touch()"
          />
          <FieldError
            v-if="formErrors.endPoint"
            :errors="[formErrors.endPoint]"
          />
        </Field>

        <Field :data-invalid="!!formErrors.timeOnPage">
          <FieldLabel :for="`${uid}-time`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TIME_ON_PAGE.LABEL') }}
          </FieldLabel>
          <Input
            :id="`${uid}-time`"
            v-model="state.timeOnPage"
            type="number"
            :placeholder="
              t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.TIME_ON_PAGE.PLACEHOLDER')
            "
            :aria-invalid="!!formErrors.timeOnPage"
            @blur="v$.timeOnPage.$touch()"
          />
          <FieldError
            v-if="formErrors.timeOnPage"
            :errors="[formErrors.timeOnPage]"
          />
        </Field>
      </FieldGroup>
    </FoldingGroup>

    <FoldingGroup
      :title="t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.TITLE')"
    >
      <FieldGroup class="gap-4">
        <Field orientation="horizontal">
          <FieldLabel :for="`${uid}-enabled`">
            {{ t('CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.ENABLED') }}
          </FieldLabel>
          <Switch :id="`${uid}-enabled`" v-model="state.enabled" />
        </Field>
        <Field orientation="horizontal">
          <FieldLabel :for="`${uid}-hours`">
            {{
              t(
                'CAMPAIGN.LIVE_CHAT.CREATE.FORM.OTHER_PREFERENCES.TRIGGER_ONLY_BUSINESS_HOURS'
              )
            }}
          </FieldLabel>
          <Switch
            :id="`${uid}-hours`"
            v-model="state.triggerOnlyDuringBusinessHours"
          />
        </Field>
      </FieldGroup>
    </FoldingGroup>
  </form>
</template>
